import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';
import { ApplicationStatus } from '../../types';
import {
  emailInterviewKit,
  downloadInterviewKit,
  getMe,
} from '../../services/api';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { interviewStageLabel } from '../../utils/interviewKit';
import { triggerBrowserDownload } from '../../utils/download';
import { captureEvent } from '../../services/analytics';
import css from './InterviewKitModal.module.scss';

interface InterviewKitModalProps {
  open: boolean;
  applicationId: string;
  roleTitle: string;
  companyName: string;
  status: ApplicationStatus;
  onClose: () => void;
}

type Stage = 'choose' | 'working' | 'result';
type Delivery = 'email' | 'download';
type ResultKind = 'email' | 'download' | 'error';

// Cache the account email across opens — it never changes within a session, and
// re-fetching on every open would flash a placeholder. (Cleared on a fresh load,
// which also covers admin impersonation switching the effective user.)
let cachedAccountEmail: string | null = null;

// ---- inline icons (stroke currentColor so they inherit the brand palette) ----
const ChecklistIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
    <path d="M4 4h11l5 5v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
    <path d="M14 4v6h6" />
    <path d="M7 13h8M7 16h6" />
  </svg>
);
const ClockIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 4v4h4" />
  </svg>
);
const LockIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <rect x="4" y="10" width="16" height="11" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);
const EnvelopeIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
    <path d="M3 7l9 6 9-6" />
    <rect x="3" y="5" width="18" height="14" rx="2" />
  </svg>
);
const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
    <path d="M12 3v12" />
    <path d="M7 11l5 5 5-5" />
    <path d="M5 21h14" />
  </svg>
);
const AlertIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
    <path d="M12 8v5" />
    <circle cx="12" cy="16.5" r=".4" fill="currentColor" stroke="none" />
    <path d="M10.3 4.3 2.6 18a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" />
  </svg>
);

const InterviewKitModal: React.FC<InterviewKitModalProps> = ({
  open,
  applicationId,
  roleTitle,
  companyName,
  status,
  onClose,
}) => {
  const [stage, setStage] = useState<Stage>('choose');
  const [delivery, setDelivery] = useState<Delivery>('email');
  const [result, setResult] = useState<ResultKind>('email');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [lastAttempt, setLastAttempt] = useState<Delivery>('email');
  const [downloadFilename, setDownloadFilename] = useState<string>('interview-kit.pdf');
  const [email, setEmail] = useState<string | null>(cachedAccountEmail);
  // Email returns a 202 in well under a second, so it never gets the full
  // "working" screen (that's the download path's synchronous render). Instead
  // the triggering button shows a brief in-flight state.
  const [submitting, setSubmitting] = useState(false);

  const emailOptRef = useRef<HTMLDivElement>(null);
  const downloadOptRef = useRef<HTMLDivElement>(null);

  const stageLabel = interviewStageLabel(status);

  // On each open: reset to a fresh "choose" state (this component can stay
  // mounted between opens) and resolve the recipient — the user's own account
  // email — caching it for the session.
  useEffect(() => {
    if (!open) return;
    setStage('choose');
    setDelivery('email');
    setErrorMsg(null);
    if (cachedAccountEmail) {
      setEmail(cachedAccountEmail);
      return;
    }
    let cancelled = false;
    getMe()
      .then((user) => {
        cachedAccountEmail = user.username;
        if (!cancelled) setEmail(user.username);
      })
      .catch(() => {
        /* non-fatal: we just fall back to "your account inbox" copy */
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const runEmail = async () => {
    setLastAttempt('email');
    setSubmitting(true); // brief in-flight state on the button; no full working screen
    try {
      await emailInterviewKit(applicationId);
      captureEvent('interview_kit_generated', {
        delivery_mode: 'email',
        application_status: status,
      });
      setResult('email');
      setStage('result');
    } catch (error) {
      setErrorMsg(getApiErrorMessage(error));
      setResult('error');
      setStage('result');
    } finally {
      setSubmitting(false);
    }
  };

  const runDownload = async () => {
    setLastAttempt('download');
    setStage('working');
    const start = performance.now();
    try {
      const { blob, filename } = await downloadInterviewKit(applicationId);
      triggerBrowserDownload(blob, filename);
      setDownloadFilename(filename);
      captureEvent('interview_kit_generated', {
        delivery_mode: 'download',
        application_status: status,
        generation_ms: Math.round(performance.now() - start),
      });
      setResult('download');
      setStage('result');
    } catch (error) {
      setErrorMsg(getApiErrorMessage(error));
      setResult('error');
      setStage('result');
    }
  };

  const handleGenerate = () => (delivery === 'email' ? runEmail() : runDownload());
  const handleRetry = () => (lastAttempt === 'email' ? runEmail() : runDownload());
  const handleEmailInstead = () => {
    setDelivery('email');
    runEmail();
  };

  const pick = (which: Delivery) => setDelivery(which);
  const optKeyDown = (e: React.KeyboardEvent, which: Delivery) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      pick(which);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next: Delivery = which === 'email' ? 'download' : 'email';
      pick(next);
      // Move DOM focus with selection — the WAI-ARIA radiogroup roving-tabindex
      // pattern requires focus and selection to travel together.
      (next === 'email' ? emailOptRef : downloadOptRef).current?.focus();
    }
  };

  // antd handles overlay / focus-trap / scroll-lock. Block dismissal mid-build.
  const dismissable = stage !== 'working';

  return (
    <Modal
      open={open}
      onCancel={dismissable ? onClose : undefined}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={dismissable}
      width="min(600px, 94vw)"
      centered
      destroyOnClose
      className={css.modal}
      // Name antd's own dialog (rc-dialog only sets aria-labelledby when a title
      // is present). The header is visually collapsed so only our card shows —
      // a single, named dialog rather than a nested one.
      title={<span className={css.srOnly}>Interview Kit</span>}
      styles={{
        header: { padding: 0, margin: 0, minHeight: 0, border: 'none', background: 'transparent' },
        content: { padding: 0, background: 'transparent', boxShadow: 'none' },
      }}
    >
      <div className={`${css.kit} ${css.isOpen}`}>
        {/* Screen-reader announcements for the otherwise-silent async transitions. */}
        <div className={css.srOnly} role="status" aria-live="polite">
          {stage === 'working' &&
            'Building your interview kit. This usually takes 5 to 20 seconds.'}
          {stage === 'result' &&
            result === 'email' &&
            `Your interview kit is on its way to ${email ?? 'your account inbox'}.`}
          {stage === 'result' &&
            result === 'download' &&
            `Your interview kit downloaded as ${downloadFilename}.`}
        </div>
        <div className={css.srOnly} role="alert">
          {stage === 'result' && result === 'error'
            ? errorMsg ?? "Couldn't generate your interview kit. Please try again."
            : ''}
        </div>

        {/* ============ CHOOSE ============ */}
        {stage === 'choose' && (
          <>
            <header className={css.head}>
              <div className={css.metaTop}>
                <span className={css.eyebrow}>
                  <span className={css.eyebrowIcon}>
                    <ChecklistIcon />
                  </span>
                  Personalized prep
                </span>
                <span className={css.stageTag}>{stageLabel}</span>
              </div>
              <h2 className={css.title} id="ik-title">
                Your interview kit
              </h2>
              <p className={css.subtitle}>
                A 1–2 page briefing for your <b>{roleTitle}</b> interview at <b>{companyName}</b> —
                built for this exact conversation, in one click.
              </p>
            </header>

            <div className={css.body}>
              <p className={css.sectionLabel}>Inside the kit</p>

              {/* HERO — age-bias, leads the pitch */}
              <div className={css.heroCard}>
                <span className={css.heroFlag}>
                  <span className={css.dot} />
                  The standout · answered for you
                </span>
                <div className={css.heroTitle}>Likely questions — including the loaded ones</div>
                <p className={css.heroDesc}>
                  Predicted interview questions with suggested answers. We surface the{' '}
                  <em>age-coded</em> ones other prep tools pretend don't exist — flagged, and
                  answered with composure.
                </p>
                <div className={css.heroQ}>
                  <div className={css.q}>
                    <span className={css.qText}>Aren't you a little overqualified for this?</span>
                    <span className={css.qTag}>Age-coded</span>
                  </div>
                  <div className={css.q}>
                    <span className={css.qText}>How do you stay current with the latest tools?</span>
                    <span className={css.qTag}>Age-coded</span>
                  </div>
                  <div className={css.q}>
                    <span className={css.qText}>Can you keep up with our pace?</span>
                    <span className={css.qTag}>Age-coded</span>
                  </div>
                </div>
              </div>

              <ol className={css.contents}>
                <li className={css.item}>
                  <span className={css.num}>1</span>
                  <div>
                    <div className={css.itemTitle}>Snapshot</div>
                    <p className={css.itemDesc}>
                      Role, company, recruiter, stage, and a one-line read on exactly why you fit.
                    </p>
                  </div>
                </li>
                <li className={css.item}>
                  <span className={css.num}>2</span>
                  <div>
                    <div className={css.itemTitle}>Company in 60 seconds</div>
                    <p className={css.itemDesc}>
                      The quick facts worth knowing before you walk in — no homework required.
                    </p>
                  </div>
                </li>
                <li className={css.item}>
                  <span className={css.num}>3</span>
                  <div>
                    <div className={css.itemTitle}>Lead with these</div>
                    <p className={css.itemDesc}>
                      The strengths from your own history worth emphasizing for this room.
                    </p>
                  </div>
                </li>
                <li className={css.item}>
                  <span className={css.num}>4</span>
                  <div>
                    <div className={css.itemTitle}>Questions to ask them</div>
                    <p className={css.itemDesc}>
                      Sharp, tailored questions that signal seniority and genuine interest.
                    </p>
                  </div>
                </li>
              </ol>

              <div className={css.delivery}>
                <p className={css.sectionLabel}>How should we send it?</p>
                <div className={css.options} role="radiogroup" aria-label="Delivery method">
                  <div
                    ref={emailOptRef}
                    className={`${css.option} ${delivery === 'email' ? css.selected : ''}`}
                    role="radio"
                    aria-checked={delivery === 'email'}
                    tabIndex={delivery === 'email' ? 0 : -1}
                    onClick={() => pick('email')}
                    onKeyDown={(e) => optKeyDown(e, 'email')}
                  >
                    <span className={css.radio} aria-hidden="true" />
                    <div className={css.optMain}>
                      <div className={css.optName}>
                        Email it to me <span className={css.recommended}>Recommended</span>
                      </div>
                      <p className={css.optDesc}>
                        {email ? (
                          <>
                            Lands in your inbox at <span className={css.addr}>{email}</span> in a
                            minute or two. Confirms instantly.
                          </>
                        ) : (
                          <>Lands in your account inbox in a minute or two. Confirms instantly.</>
                        )}
                      </p>
                    </div>
                    <div className={css.badge}>
                      <span className={css.badgeMain}>Instant</span>
                      <span className={css.badgeSub}>confirmation</span>
                    </div>
                  </div>

                  <div
                    ref={downloadOptRef}
                    className={`${css.option} ${delivery === 'download' ? css.selected : ''}`}
                    role="radio"
                    aria-checked={delivery === 'download'}
                    tabIndex={delivery === 'download' ? 0 : -1}
                    onClick={() => pick('download')}
                    onKeyDown={(e) => optKeyDown(e, 'download')}
                  >
                    <span className={css.radio} aria-hidden="true" />
                    <div className={css.optMain}>
                      <div className={css.optName}>Download the PDF</div>
                      <p className={css.optDesc}>
                        Generated live and rendered to PDF right here — stay on this screen while it
                        builds.
                      </p>
                    </div>
                    <div className={`${css.badge} ${css.warn}`}>
                      <span className={css.badgeMain}>~5–20 sec</span>
                      <span className={css.badgeSub}>live render</span>
                    </div>
                  </div>
                </div>

                <p className={css.reassure}>
                  <span className={css.lock} aria-hidden="true">
                    <LockIcon />
                  </span>
                  <span>
                    This goes <b>only to you</b>. It is never sent to the recruiter or anyone at the
                    company — it's your prep, kept private.
                  </span>
                </p>
              </div>
            </div>

            <footer className={css.foot}>
              <p className={css.footNote}>
                Each kit is a <b>fresh AI generation</b>, written for this interview.
              </p>
              <div className={css.actions}>
                <button className={css.btnGhost} type="button" onClick={onClose} disabled={submitting}>
                  Cancel
                </button>
                <button
                  className={css.btnPrimary}
                  type="button"
                  onClick={handleGenerate}
                  disabled={submitting}
                >
                  <span className={css.seal} aria-hidden="true">
                    <ChecklistIcon />
                  </span>
                  {submitting
                    ? 'Sending…'
                    : delivery === 'email'
                    ? 'Generate & email my kit'
                    : 'Generate & download'}
                </button>
              </div>
            </footer>
          </>
        )}

        {/* ============ WORKING ============ */}
        {stage === 'working' && (
          <>
            <div className={css.working}>
              <div className={css.builder} aria-hidden="true">
                <div className={css.scan} />
                <div className={`${css.line} ${css.accent}`} />
                <div className={css.line} />
                <div className={css.line} />
                <div className={`${css.line} ${css.accent}`} />
                <div className={css.line} />
                <div className={css.line} />
              </div>
              <h2 className={css.workTitle}>Building your interview kit…</h2>
              <p className={css.workDesc}>
                We're writing your briefing and rendering it to PDF — drafting your answers to the
                loaded questions takes a beat. Worth the few seconds.
              </p>
              <div className={css.progress} aria-hidden="true">
                <div className={css.bar} />
              </div>
              <p className={css.latency}>Usually 5–20 seconds · keep this open</p>
            </div>
            <footer className={css.foot}>
              <p className={css.footNote}>Generating one kit — don't refresh.</p>
              <div className={css.actions}>
                <button className={css.btnPrimary} type="button" disabled>
                  <span className={css.seal} aria-hidden="true">
                    <ClockIcon />
                  </span>
                  Building…
                </button>
              </div>
            </footer>
          </>
        )}

        {/* ============ RESULT ============ */}
        {stage === 'result' && (
          <>
            <div className={css.result}>
              {result === 'email' && (
                <>
                  <span className={`${css.statusChip} ${css.ok}`}>Queued</span>
                  <div className={`${css.bigSeal} ${css.sealOk}`} aria-hidden="true">
                    <EnvelopeIcon />
                  </div>
                  <h2 className={css.resultTitle}>On its way to you</h2>
                  <p className={css.resultText}>
                    Your interview kit is headed to{' '}
                    <span className={css.addrStrong}>{email ?? 'your account inbox'}</span>. It
                    usually lands within a minute or two.
                  </p>
                  <p className={css.hint}>
                    Didn't arrive? Give it a moment, then check your spam folder — or send it again.
                  </p>
                  <div className={css.resultActions}>
                    <button
                      className={css.btnPrimary}
                      type="button"
                      onClick={runEmail}
                      disabled={submitting}
                    >
                      <span className={css.seal} aria-hidden="true">
                        <RefreshIcon />
                      </span>
                      {submitting ? 'Sending…' : 'Send again'}
                    </button>
                  </div>
                </>
              )}

              {result === 'download' && (
                <>
                  <span className={`${css.statusChip} ${css.ok}`}>Saved</span>
                  <div className={`${css.bigSeal} ${css.sealOk}`} aria-hidden="true">
                    <DownloadIcon />
                  </div>
                  <h2 className={css.resultTitle}>Your kit is downloaded</h2>
                  <p className={css.resultText}>
                    Saved as <span className={css.addrStrong}>{downloadFilename}</span>. Open it
                    whenever you're ready to prep.
                  </p>
                  <p className={css.hint}>Can't find it? Check your browser's Downloads folder.</p>
                  <div className={css.resultActions}>
                    <button
                      className={css.btnPrimary}
                      type="button"
                      onClick={() => setStage('choose')}
                    >
                      <span className={css.seal} aria-hidden="true">
                        <RefreshIcon />
                      </span>
                      Generate again
                    </button>
                  </div>
                </>
              )}

              {result === 'error' && (
                <>
                  <span className={`${css.statusChip} ${css.err}`}>Error</span>
                  <div className={`${css.bigSeal} ${css.sealErr}`} aria-hidden="true">
                    <AlertIcon />
                  </div>
                  <h2 className={css.resultTitle}>Couldn't generate your kit</h2>
                  <p className={css.resultText}>
                    {errorMsg ?? "Something failed during the live render. Nothing's lost."}
                  </p>
                  <p className={css.hint}>Not your fault — this is usually temporary.</p>
                  <div className={css.resultActions}>
                    {lastAttempt === 'download' && (
                      <button
                        className={css.btnGhost}
                        type="button"
                        onClick={handleEmailInstead}
                        disabled={submitting}
                      >
                        Email it to me instead
                      </button>
                    )}
                    <button
                      className={css.btnPrimary}
                      type="button"
                      onClick={handleRetry}
                      disabled={submitting}
                    >
                      <span className={css.seal} aria-hidden="true">
                        <RefreshIcon />
                      </span>
                      {submitting ? 'Sending…' : 'Try again'}
                    </button>
                  </div>
                </>
              )}
            </div>
            <footer className={css.foot}>
              <p className={css.footNote}>
                Kept private · <b>for your eyes only</b>.
              </p>
              <div className={css.actions}>
                <button className={css.btnGhost} type="button" onClick={onClose}>
                  Close
                </button>
              </div>
            </footer>
          </>
        )}
      </div>
    </Modal>
  );
};

export default InterviewKitModal;
